import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { HierarchiesService, type Hierarchy } from '@/services/modules/HierarchiesService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

const FULL_ACCESS = ['*', 'application.*']

export function HierarchiesPage() {
  const nav = useNavigate()
  const { can } = useAuthority()
  const canCreate = can('administractive.hierarchies.create')
  const canEdit = can('administractive.hierarchies.edit')
  const canDelete = can('administractive.hierarchies.delete')
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar hierarquia' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(HierarchiesService, { size: 10, reloadKey: reload, filters: sf.filters })

  const exp = useDataTableExport({
    filename: 'hierarchies',
    service: HierarchiesService as never,
    filters: sf.filters,
    columns: [
      { header: 'Nome', accessor: (h: Hierarchy) => h.name },
      { header: 'Permissões', accessor: (h: Hierarchy) => String(h.authorities?.length ?? 0) },
      { header: 'Status', accessor: (h: Hierarchy) => h.status },
    ],
  })
  const [openDelete, setOpenDelete] = useState<Hierarchy | null>(null)

  async function handleDelete() {
    if (!openDelete) return
    try { await HierarchiesService.destroy(openDelete.id); toast.success('Hierarquia removida.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  const columns: DataTableColumn<Hierarchy>[] = [
    { key: 'name', header: 'Nome', render: h => <span className="font-medium text-gray-800">{h.name}</span>, sortable: true },
    { key: 'authorities', header: 'Permissões', render: h => {
      const auths = h.authorities ?? []
      const isFull = auths.some(a => FULL_ACCESS.includes(a))
      if (isFull) return <Badge variant="violet">Acesso total</Badge>
      if (auths.length === 0) return <span className="text-gray-400 text-xs">Nenhuma</span>
      return (
        <span className="inline-flex items-center gap-1.5 text-gray-700">
          <ShieldCheck size={14} className="text-brand-500" />
          <span className="text-sm">{auths.length} permiss{auths.length === 1 ? 'ão' : 'ões'}</span>
        </span>
      )
    } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: h => <StatusDot value={h.status} /> },
  ]

  return (
    <>
      <PageHeader title="Hierarquias" subtitle="Grupos de permissão atribuídos aos usuários"
        crumbs={[{ label: 'Administração' }, { label: 'Hierarquias' }]}
        actions={canCreate ? <Button onClick={() => nav('/administractive/hierarchies/novo')} iconLeft={<Plus size={14} />}>Nova hierarquia</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="administractive.hierarchies"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(h) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => nav(`/administractive/hierarchies/${h.id}/editar`) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(h) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover hierarquia?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"?`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
